import React, { Suspense, lazy } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiCheck, FiLock } from "react-icons/fi";
import { usePlan } from "../utils/usePlan";

// Loaded only when a locked page is shown, so paying users never download it.
const LockedDemo = lazy(() => import("./LockedDemo"));
const DEMO_PAGES = new Set(["jobs", "watchlist", "outreach", "research"]);

/* A small "part of the Growth plan" tag with a link to the plans page, placed next to
   anything the current plan does not include. */
const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg2 || theme.bg};
  color: ${({ theme }) => theme.inkSoft || theme.inkMuted};
  font: inherit;
  font-size: 12px;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;
  /* Plan limits are orange (theme.secondary); blue is for actions. */
  b {
    font-weight: 600;
    color: ${({ theme }) => theme.secondary};
  }
  &:hover {
    border-color: ${({ theme }) => theme.secondary};
  }
`;

export default function PlanLock({ plan = "Growth", label, style }) {
  const navigate = useNavigate();
  return (
    <Tag
      role="link"
      tabIndex={0}
      style={style}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate("/plans");
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          navigate("/plans");
        }
      }}
      title={`${label || "This"} is part of the ${plan} plan and up`}
    >
      <FiLock size={11} aria-hidden="true" style={{ color: "var(--secondary)" }} />
      {plan} plan <b>Upgrade</b>
    </Tag>
  );
}

/* A full-width notice for a whole section that the plan does not include. */
const Panel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg2 || theme.bg};
  h3 {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme.ink};
  }
  p {
    margin: 0;
    font-size: 13.5px;
    color: ${({ theme }) => theme.inkMuted};
    line-height: 1.5;
  }
  button {
    appearance: none;
    flex: none;
    height: 36px;
    padding: 0 16px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.onFill || "#fff"};
    font: inherit;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
  }
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

/* A page the plan doesn't include: a static blurred sketch of it behind one card that explains it. */
const LOCK_COPY = {
  jobs: {
    title: "Track hiring at your client firms",
    text: "New openings at the companies you recruit for, collected every day.",
    points: ["Add any company's careers page", "Pull from LinkedIn, JobsDB and eFinancialCareers", "New roles arrive every day", "Filter by country, function and seniority"],
  },
  watchlist: {
    title: "Keep the jobs and firms you care about",
    text: "Save jobs and companies from Jobs and Research, and come back to them here.",
    points: ["One list for saved jobs and firms", "See new roles at saved firms first", "Daily email when they post"],
  },
  outreach: {
    title: "Reach candidates from your own inbox",
    text: "Email the people you find from your own mailbox, with help on every draft.",
    points: ["An email written by AI for each candidate", "Reusable templates for every kind of outreach", "Send from your own mailbox, replies in one place"],
  },
  research: {
    title: "Know a firm before you call",
    text: "Company profiles, news and hiring contacts for the firms you track.",
    points: ["Profile, size and hiring at a glance", "Recent news, summarised", "HR and hiring contacts on request"],
  },
};

const Gate = styled.div`
  position: relative;
  min-height: calc(100vh - 5rem);
  overflow: hidden;
  isolation: isolate;
`;

const Sketch = styled.div`
  position: absolute;
  inset: 0;
  filter: blur(4px);
  opacity: 0.55;
  pointer-events: none;
  user-select: none;
  /* Dims the sketch towards the bottom so the card reads first, but keeps it to the edge. */
  mask-image: linear-gradient(to bottom, #000 40%, rgba(0, 0, 0, 0.45) 100%);
  -webkit-mask-image: linear-gradient(to bottom, #000 40%, rgba(0, 0, 0, 0.45) 100%);
`;

const BAR_TONE = { ink: "lineStrong", accent: "accentSoft", orange: "secondarySoft" };
const Bar = styled.div`
  height: ${({ $h }) => $h || 10}px;
  width: ${({ $w }) => $w || "100%"};
  border-radius: 999px;
  background: ${({ $tone, theme }) => theme[BAR_TONE[$tone] || "line"]};
`;

const SketchCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  > .logo {
    flex: none;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${({ theme }) => theme.bg2};
  }
  > .body {
    flex: 1;
    display: grid;
    gap: 8px;
  }
`;

const Row = styled.div`
  display: flex;
  gap: ${({ $gap }) => $gap || 10}px;
  align-items: ${({ $align }) => $align || "center"};
`;

const Stack = styled.div`
  display: grid;
  gap: ${({ $gap }) => $gap || 10}px;
  flex: ${({ $flex }) => $flex || "initial"};
  align-content: start;
`;

// Deterministic widths so the sketch looks varied but never jumps between renders.
const W = ["62%", "48%", "71%", "55%", "66%", "44%", "58%", "69%"];
const W2 = ["34%", "40%", "28%", "37%", "31%", "42%"];

function SketchRows({ n = 7, tags = true }) {
  return Array.from({ length: n }, (_, i) => (
    <SketchCard key={i}>
      <span className="logo" />
      <span className="body">
        <Bar $h={11} $w={W[i % W.length]} $tone="ink" />
        <Bar $h={8} $w={W2[i % W2.length]} />
      </span>
      {tags && (
        <Row $gap={6}>
          <Bar $h={20} $w="64px" $tone={i % 3 === 0 ? "orange" : "accent"} />
          <Bar $h={20} $w="48px" />
        </Row>
      )}
    </SketchCard>
  ));
}

// Mirrors the Jobs page tiles (JCard in Jobs.js) without importing it, which would be circular.
const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 14px;
`;

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 22px 18px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  .logo {
    flex: none;
    width: 40px;
    height: 40px;
    border-radius: 11px;
    background: ${({ theme }) => theme.bg2};
  }
  .foot {
    margin-top: 22px;
    padding-top: 14px;
    border-top: 1px solid ${({ theme }) => theme.lineSoft || theme.line};
    display: flex;
    justify-content: space-between;
  }
`;

function SketchTiles({ n = 18 }) {
  return (
    <TileGrid>
      {Array.from({ length: n }, (_, i) => (
        <Tile key={i}>
          <Row $gap={12} style={{ marginBottom: 16 }}>
            <span className="logo" />
            <Stack $gap={7} $flex={1}>
              <Bar $h={12} $w={W[i % W.length]} />
              <Bar $h={10} $w={W2[i % W2.length]} />
            </Stack>
          </Row>
          <Stack $gap={8}>
            <Bar $h={16} $w="90%" $tone="ink" />
            <Bar $h={16} $w={W[(i + 2) % W.length]} $tone="ink" />
          </Stack>
          <Stack $gap={7} style={{ marginTop: 14 }}>
            <Bar $h={9} $w="100%" />
            <Bar $h={9} $w="78%" />
          </Stack>
          <Row $gap={6} style={{ marginTop: 16 }}>
            <Bar $h={22} $w="72px" $tone={i % 3 === 0 ? "orange" : "accent"} />
            <Bar $h={22} $w="92px" />
          </Row>
          <div className="foot">
            <Bar $h={28} $w="96px" />
            <Bar $h={28} $w="84px" $tone="accent" />
          </div>
        </Tile>
      ))}
    </TileGrid>
  );
}

function PageSketch({ page }) {
  const head = (
    <Stack $gap={14} style={{ marginBottom: 24 }}>
      <Bar $h={22} $w="220px" $tone="ink" />
      <Row $gap={8}>
        <Bar $h={30} $w="96px" $tone="orange" />
        <Bar $h={30} $w="96px" />
        <Bar $h={30} $w="96px" />
        <span style={{ flex: 1 }} />
        <Bar $h={36} $w="260px" />
      </Row>
    </Stack>
  );
  if (page === "outreach") {
    return (
      <>
        {head}
        <Row $gap={16} $align="stretch">
          <Stack $gap={10} style={{ width: 340 }}>
            <SketchRows n={24} tags={false} />
          </Stack>
          <SketchCard style={{ flex: 1, alignItems: "flex-start", minHeight: "calc(100vh - 12rem)" }}>
            <Stack $gap={14} $flex={1}>
              <Row $gap={14}>
                <span className="logo" style={{ width: 56, height: 56, borderRadius: 999 }} />
                <Stack $gap={8} $flex={1}>
                  <Bar $h={14} $w="40%" $tone="ink" />
                  <Bar $h={9} $w="28%" />
                </Stack>
              </Row>
              {W.slice(0, 6).map((w, i) => <Bar key={i} $h={9} $w={w} />)}
              <Bar $h={36} $w="140px" $tone="accent" />
            </Stack>
          </SketchCard>
        </Row>
      </>
    );
  }
  if (page === "research") {
    return (
      <>
        {head}
        <Row $gap={16} $align="stretch" style={{ flexWrap: "wrap" }}>
          {Array.from({ length: 18 }, (_, i) => (
            <SketchCard key={i} style={{ flex: "1 1 300px", alignItems: "flex-start", minHeight: 170 }}>
              <span className="logo" />
              <Stack $gap={10} $flex={1}>
                <Bar $h={12} $w={W[i % W.length]} $tone="ink" />
                <Bar $h={8} $w="90%" />
                <Bar $h={8} $w="75%" />
                <Row $gap={6}>
                  <Bar $h={20} $w="70px" $tone="accent" />
                  <Bar $h={20} $w="54px" />
                </Row>
              </Stack>
            </SketchCard>
          ))}
        </Row>
      </>
    );
  }
  return (
    <>
      {head}
      {page === "jobs" ? (
        <SketchTiles />
      ) : (
        <Stack $gap={10}>
          <SketchRows n={24} />
        </Stack>
      )}
    </>
  );
}

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: min(460px, calc(100% - 32px));
  margin: max(64px, 14vh) auto 48px;
  padding: 32px 32px 26px;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${({ theme }) => theme.shadowLg};
  text-align: center;
  animation: planGateIn 0.28s ease-out both;
  @keyframes planGateIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: none; }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  .eyebrow {
    display: inline-block;
    margin-bottom: 10px;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.secondary};
  }
  h2 {
    margin: 0 0 8px;
    font-size: 22px;
    line-height: 1.25;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.ink};
  }
  > p {
    margin: 0 auto;
    max-width: 360px;
    font-size: 14px;
    line-height: 1.55;
    color: ${({ theme }) => theme.inkSoft || theme.inkMuted};
  }
  ul {
    list-style: none;
    margin: 20px 0 24px;
    padding: 16px 18px;
    display: grid;
    gap: 10px;
    text-align: left;
    border-radius: 12px;
    background: ${({ theme }) => theme.surface2 || theme.bg};
    border: 1px solid ${({ theme }) => theme.lineSoft || theme.line};
  }
  li {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 13.5px;
    line-height: 1.45;
    color: ${({ theme }) => theme.ink2 || theme.ink};
    svg {
      flex: none;
      margin-top: 2px;
      color: ${({ theme }) => theme.accent};
    }
  }
  .actions {
    display: grid;
    gap: 8px;
  }
  .primary,
  .ghost {
    appearance: none;
    height: 42px;
    border-radius: 999px;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .primary {
    border: 1px solid ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.onFill || "#fff"};
    &:hover { background: ${({ theme }) => theme.accentHover || theme.accent}; }
  }
  .ghost {
    border: 1px solid transparent;
    background: transparent;
    color: ${({ theme }) => theme.inkSoft || theme.inkMuted};
    &:hover { color: ${({ theme }) => theme.ink}; background: ${({ theme }) => theme.bg2}; }
  }
  .note {
    margin: 14px 0 0;
    font-size: 12px;
    color: ${({ theme }) => theme.inkMuted};
  }
`;

// Holds the demo's space while its chunk loads, so the card doesn't jump.
const DemoSlot = styled.div`
  aspect-ratio: 640 / 400;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.bg};
  margin-bottom: 50px;
`;

/* The wider card for pages with an animated walkthrough: the demo carries the feature list. */
const DemoCard = styled.div`
  position: relative;
  z-index: 1;
  width: min(760px, calc(100% - 32px));
  margin: max(32px, 6vh) auto 48px;
  padding: 28px 28px 22px;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  box-shadow: ${({ theme }) => theme.shadowLg};
  box-sizing: border-box;
  animation: planGateIn 0.28s ease-out both;
  @keyframes planGateIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: none; }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  .plan {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    font-weight: 500;
    color: ${({ theme }) => theme.secondary};
  }
  h2 {
    margin: 8px 0 6px;
    font-family: var(--font-display);
    font-size: 34px;
    font-weight: 400;
    line-height: 1.1;
    color: ${({ theme }) => theme.ink};
  }
  .lede {
    margin: 0 0 20px;
    max-width: 520px;
    font-size: 14.5px;
    line-height: 1.55;
    color: ${({ theme }) => theme.inkSoft || theme.inkMuted};
  }
  .foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid ${({ theme }) => theme.lineSoft || theme.line};
    p {
      margin: 0;
      font-size: 12.5px;
      color: ${({ theme }) => theme.inkMuted};
    }
  }
  .actions {
    display: flex;
    gap: 8px;
  }
  .primary,
  .ghost {
    appearance: none;
    height: 40px;
    padding: 0 18px;
    border-radius: 999px;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    white-space: nowrap;
  }
  .primary {
    border: 1px solid ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.onFill || "#fff"};
    &:hover { background: ${({ theme }) => theme.accentHover || theme.accent}; }
  }
  .ghost {
    border: 1px solid transparent;
    background: transparent;
    color: ${({ theme }) => theme.inkSoft || theme.inkMuted};
    &:hover { color: ${({ theme }) => theme.ink}; background: ${({ theme }) => theme.bg2}; }
  }
  .primary:focus-visible,
  .ghost:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
  @media (max-width: 640px) {
    padding: 22px 18px 18px;
    h2 { font-size: 28px; }
    .foot { flex-direction: column; align-items: stretch; }
    .actions { flex-direction: column-reverse; }
  }
`;

export function PlanGate({ feature, page, children }) {
  const { features } = usePlan();
  const navigate = useNavigate();
  if (features[feature] !== false) return children;
  const key = page || feature;
  const copy = LOCK_COPY[key] || LOCK_COPY.jobs;
  if (DEMO_PAGES.has(key)) {
    return (
      <Gate>
        <Sketch aria-hidden="true">
          <PageSketch page={key} />
        </Sketch>
        <DemoCard role="region" aria-label={copy.title}>
          <span className="plan">
            <FiLock size={12} aria-hidden="true" /> Starter plan and up
          </span>
          <h2>{copy.title}</h2>
          <p className="lede">{copy.text}</p>
          <Suspense fallback={<DemoSlot />}>
            <LockedDemo page={key} steps={copy.points} />
          </Suspense>
          <div className="foot">
            <p>Your free trial includes Find candidates.</p>
            <div className="actions">
              <button type="button" className="ghost" onClick={() => navigate("/find-candidates")}>
                Back to Find candidates
              </button>
              <button type="button" className="primary" onClick={() => navigate("/plans")}>
                See plans <FiArrowRight size={15} aria-hidden="true" />
              </button>
            </div>
          </div>
        </DemoCard>
      </Gate>
    );
  }
  return (
    <Gate>
      <Sketch aria-hidden="true">
        <PageSketch page={key} />
      </Sketch>
      <Card role="region" aria-label={copy.title}>
        <span className="eyebrow">Starter plan and up</span>
        <h2>{copy.title}</h2>
        <p>{copy.text}</p>
        <ul>
          {copy.points.map((pt) => (
            <li key={pt}>
              <FiCheck size={15} aria-hidden="true" />
              {pt}
            </li>
          ))}
        </ul>
        <div className="actions">
          <button type="button" className="primary" onClick={() => navigate("/plans")}>
            See plans <FiArrowRight size={15} aria-hidden="true" />
          </button>
          <button type="button" className="ghost" onClick={() => navigate("/find-candidates")}>
            Back to Find candidates
          </button>
        </div>
        <p className="note">Your free trial includes Find candidates.</p>
      </Card>
    </Gate>
  );
}

export function PlanLockPanel({ plan = "Growth", title, text }) {
  const navigate = useNavigate();
  return (
    <Panel role="note">
      <div>
        <h3>
          <FiLock size={13} style={{ marginRight: 6, verticalAlign: -1, color: "var(--secondary)" }} aria-hidden="true" />
          {title || `Part of the ${plan} plan`}
        </h3>
        <p>{text || `Move to ${plan} to switch this on. Changes apply straight away.`}</p>
      </div>
      <button type="button" onClick={() => navigate("/plans")}>
        See plans
      </button>
    </Panel>
  );
}
